import { AuthApiResponse, AuthResult, LoginParams } from './types';

const DEFAULT_API_URL = 'https://api.walkycam.com/v1';
const DEFAULT_API_KEY = 'n8d3ePgFaUYGxqBRZzVfkDSKolJ9hw72';

export const getApiBaseUrl = (): string => {
  const url = import.meta.env.VITE_API_URL || import.meta.env.VITE_BASE_URL || DEFAULT_API_URL;
  return url.replace(/\/+$/, '');
};

export const getApiKey = (): string => {
  return import.meta.env.VITE_API_KEY || DEFAULT_API_KEY;
};

export const STORAGE_AUTH_TOKEN_KEY = 'walky_auth_token';
export const STORAGE_AUTH_USER_KEY = 'walky_auth_user';

export const mapResponseCodeToMessage = (code?: number, fallbackMessage?: string): string => {
  switch (code) {
    case 100001:
      return 'Faltan parámetros requeridos. Por favor completa tu usuario y contraseña.';
    case 100002:
      return 'Credenciales inválidas. Verifica tu usuario/correo y contraseña.';
    case 100003:
      return 'Debes cambiar tu contraseña para continuar.';
    case 100004:
      return 'Tu cuenta ha sido bloqueada. Por favor contacta al administrador.';
    case 100005:
      return 'Tu correo electrónico aún no ha sido verificado. Por favor verifícalo antes de ingresar.';
    case 100016:
      return 'Usuario no encontrado o sesión no válida.';
    default:
      return fallbackMessage || 'Ocurrió un error al procesar la solicitud.';
  }
};

export async function loginUser(params: LoginParams): Promise<AuthResult> {
  const baseUrl = getApiBaseUrl();
  const apiKey = getApiKey();

  try {
    console.log('[WalkyAuth] Sending login request for:', params.user.trim());
    const response = await fetch(`${baseUrl}/user/login`, {
      method: 'POST',
      mode: 'cors',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': apiKey,
      },
      body: JSON.stringify({
        user: params.user.trim(),
        password: params.password,
      }),
    });

    const data: AuthApiResponse = await response.json().catch(() => null);
    console.log('[WalkyAuth] Login API raw response:', data);

    if (!data) {
      return {
        success: false,
        error: 'Respuesta inválida del servidor.',
      };
    }

    const isSuccess = data.success === true || data.response_code === 100000;

    if (isSuccess) {
      const user = data.user || data.data?.user || null;
      const token = data.token || data.data?.token || '';

      if (!token) {
        return {
          success: false,
          error: 'No se recibió el token de autenticación del servidor.',
        };
      }

      return {
        success: true,
        user: user || undefined,
        token,
        response_code: data.response_code,
      };
    }

    const errorMsg = mapResponseCodeToMessage(data.response_code, data.response_message || data.message);
    return {
      success: false,
      error: errorMsg,
      response_code: data.response_code,
    };
  } catch (err) {
    console.error('[WalkyAuth] Login network error:', err);
    return {
      success: false,
      error: 'No fue posible conectar con el servidor de autenticación. Verifica tu conexión a internet.',
    };
  }
}

export async function rememberUser(token: string): Promise<AuthResult> {
  if (!token) {
    return { success: false, error: 'Token no disponible.' };
  }

  const baseUrl = getApiBaseUrl();
  const apiKey = getApiKey();

  try {
    const response = await fetch(`${baseUrl}/user/remember`, {
      method: 'POST',
      mode: 'cors',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': apiKey,
        Authorization: `Bearer ${token}`,
        auth: token,
      },
      body: JSON.stringify({ token }),
    });

    const data: AuthApiResponse = await response.json().catch(() => null);
    console.log('[WalkyAuth] Remember API raw response:', data);

    if (!data) {
      return { success: false, error: 'Respuesta inválida del servidor.' };
    }

    const isSuccess = data.success === true || data.response_code === 100000;

    if (isSuccess) {
      const user = data.user || data.data?.user || null;
      const responseToken = data.token || data.data?.token || token;

      return {
        success: true,
        user: user || undefined,
        token: responseToken,
        response_code: data.response_code,
      };
    }

    return {
      success: false,
      error: mapResponseCodeToMessage(data.response_code, data.response_message || data.message),
      response_code: data.response_code,
    };
  } catch (err) {
    console.warn('[WalkyAuth] Remember error:', err);
    return {
      success: false,
      error: 'Error al validar la sesión guardada.',
    };
  }
}
